/**
 * The visit register — `/countries/:cc/field/visits` (v17 F4).
 *
 * ===========================================================================
 * ARRIVAL AGREEMENT IS A RECORDED FACT, NOT A VERDICT
 * ===========================================================================
 * The brief's own words: "a visit that says 'arrived' from 40km away is a
 * fact worth having, not an error to refuse". So the agreement pill wears the
 * WARNING pair on a disagreement — something to look at — and never the
 * danger pair, which this design reserves for failures (a failed test round,
 * a refused save). An `undeterminable` visit says so in a neutral chip: either
 * side lacked a coordinate at arrive time, and rendering that as "disagrees"
 * would accuse an engineer whose customer simply has no saved location.
 *
 * The distance beside the pill is a VISIT-level business fact (F0 D4): it
 * ships under `field.visits.view`, unmasked, and survives the coordinates
 * purge — which is why a row can honestly show "1.2 km" beside two position
 * cells reading "Restricted". The coordinate is personal data; the derived
 * distance to the customer's premises was ruled a fact about the VISIT.
 *
 * Filters mirror the server's own (`?user_id, purpose, open, date_from/to`),
 * passed through as the URL holds them — the testing register's rule: the
 * server owns the vocabulary and answers 400 naming it; a silently-dropped
 * bad parameter would render the whole register under a URL claiming to be
 * filtered.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, EmptyState, SkeletonRows } from '../../components/ui';
import { cn } from '../../lib/cn';
import { fetchVisitRegister, fieldErrorText, fieldKeys } from './api';
import type { ArrivalAgreement, VisitFilters, VisitRow } from './api';
import {
  ArrivalDistance,
  DateRangeFilter,
  EngineerFilterRow,
  FilterChip,
  PositionCell,
  TimeCell,
  engineerOptionsOf,
} from './bits';
import { classifyAgreement } from './position';
import type { AgreementState } from './position';
import { PagerFooter } from '../renewals/PagerFooter';

const PER_PAGE = 50;

/**
 * The purpose chips — the entity's closed six, reordered for a reader (the
 * entity puts 'other' first for a MySQL enum-default reason that is not a
 * reading order; `field-registers.test.tsx` pins the SET equal to the API's).
 */
const PURPOSE_FILTERS: readonly { value: string | null; label: string }[] = [
  { value: null, label: 'All purposes' },
  { value: 'support', label: 'Support' },
  { value: 'installation', label: 'Installation' },
  { value: 'training', label: 'Training' },
  { value: 'sales', label: 'Sales' },
  { value: 'collection', label: 'Collection' },
  { value: 'other', label: 'Other' },
];

const OPEN_FILTERS: readonly { value: string | null; label: string }[] = [
  { value: null, label: 'All visits' },
  { value: 'true', label: 'On site' },
  { value: 'false', label: 'Completed' },
];

/**
 * The pill, keyed by {@link AgreementState} rather than by the raw token.
 *
 * ⚠ IT USED TO BE KEYED BY THE TOKEN, WITH A FALL-THROUGH THAT PRINTED THE
 * SERVER'S OWN WORD (v17 ruling 6 changed this). That default was reasonable
 * for an unrecognised verb and became a defect the moment the server started
 * sending `'•••'`: the register would have rendered a pill whose LABEL was the
 * redaction marker — a chip reading "•••" beside a band reading "over 5 km",
 * with no copy anywhere saying what happened. Routing through
 * `classifyAgreement` gives the masked arm a sentence of its own and leaves
 * genuinely unknown tokens on the honest 'No comparison' neutral.
 */
const AGREEMENT_PILL: Readonly<Record<AgreementState['kind'], { label: string; classes: string }>> =
  {
    agrees: { label: 'Position agrees', classes: 'bg-ok-bg text-ok-fg' },
    disagrees: { label: 'Position differs', classes: 'bg-warn-bg text-warn-fg' },
    // NOT "no comparison": one was made, and this account may not see it. The
    // BAND column beside this pill still carries the finding, which is why the
    // register is still worth reading from a restricted seat.
    restricted: { label: 'Comparison restricted', classes: 'bg-subtle text-fg ring-1 ring-line' },
    undeterminable: { label: 'No comparison', classes: 'bg-subtle text-fg ring-1 ring-line' },
  };

function agreementPill(agreement: ArrivalAgreement | string): {
  label: string;
  classes: string;
  kind: AgreementState['kind'];
} {
  const kind = classifyAgreement(agreement).kind;
  return { ...AGREEMENT_PILL[kind], kind };
}

export function VisitRegisterPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();

  const canView = can(countryCode, 'field.visits.view');

  const userId = params.get('user_id');
  const rawPurpose = params.get('purpose');
  const purpose = rawPurpose === null || rawPurpose === '' ? null : rawPurpose;
  const rawOpen = params.get('open');
  const open = rawOpen === null || rawOpen === '' ? null : rawOpen;
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  const filters = useMemo<VisitFilters>(
    () => ({
      userId: userId ?? undefined,
      purpose: purpose ?? undefined,
      open: open === null ? undefined : open === 'true',
      dateFrom: from === '' ? undefined : from,
      dateTo: to === '' ? undefined : to,
      page,
      perPage: PER_PAGE,
    }),
    [userId, purpose, open, from, to, page],
  );

  const list = useQuery({
    queryKey: fieldKeys.visits(countryCode, filters),
    queryFn: ({ signal }) => fetchVisitRegister(countryCode, filters, signal),
    enabled: canView,
  });

  if (!canView) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="Visits are not available to this account">
          Viewing the visit register in {countryCode} needs the field visits permission. Ask
          an administrator to widen your access.
        </Alert>
      </div>
    );
  }

  const rows = list.data?.data ?? [];
  const total = list.data?.total ?? 0;
  const anyFilter = userId !== null || purpose !== null || open !== null || from !== '' || to !== '';

  const patchParams = (mutate: (patch: URLSearchParams) => void): void => {
    const patch = new URLSearchParams(params);
    mutate(patch);
    patch.delete('page');
    setParams(patch, { replace: true });
  };

  const setParamOrDrop = (key: string, next: string | null): void => {
    patchParams((patch) => {
      if (next === null) patch.delete(key);
      else patch.set(key, next);
    });
  };

  const setRange = (next: { from: string; to: string }): void => {
    patchParams((patch) => {
      if (next.from === '') patch.delete('from');
      else patch.set('from', next.from);
      if (next.to === '') patch.delete('to');
      else patch.set('to', next.to);
    });
  };

  const goToPage = (next: number): void => {
    const patch = new URLSearchParams(params);
    if (next <= 1) patch.delete('page');
    else patch.set('page', String(next));
    setParams(patch);
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="Visits"
        subtitle={
          list.data === undefined
            ? undefined
            : `${String(total)} ${total === 1 ? 'visit' : 'visits'} in ${countryCode}`
        }
      />

      <div className="flex flex-col gap-2">
        <div role="group" aria-label="Filter by purpose" className="flex flex-wrap items-center gap-2">
          {PURPOSE_FILTERS.map((chip) => (
            <FilterChip
              key={chip.label}
              label={chip.label}
              active={purpose === chip.value}
              onSelect={() => setParamOrDrop('purpose', chip.value)}
            />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <div role="group" aria-label="Filter by state" className="flex flex-wrap items-center gap-2">
            {OPEN_FILTERS.map((chip) => (
              <FilterChip
                key={chip.label}
                label={chip.label}
                active={open === chip.value}
                onSelect={() => setParamOrDrop('open', chip.value)}
              />
            ))}
          </div>
          <DateRangeFilter from={from} to={to} onChange={setRange} />
        </div>
        <EngineerFilterRow
          options={engineerOptionsOf(rows)}
          active={userId}
          onSelect={(next) => setParamOrDrop('user_id', next)}
        />
      </div>

      {list.isPending ? (
        <div className="rounded-md border border-line bg-surface px-4">
          <SkeletonRows rows={6} label="Loading the visit register" />
        </div>
      ) : list.isError ? (
        <Alert tone="danger" title="The visit register could not be loaded">
          <p>{fieldErrorText(list.error)}</p>
          <button
            type="button"
            onClick={() => {
              void list.refetch();
            }}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Try again.
          </button>
        </Alert>
      ) : rows.length === 0 && total > 0 ? (
        <Alert tone="info" title={`Page ${String(list.data.page)} is past the end of this register`}>
          <p>
            The register holds {String(total)} {total === 1 ? 'visit' : 'visits'}.
          </p>
          <button
            type="button"
            onClick={() => goToPage(1)}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Show the first page.
          </button>
        </Alert>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-line bg-surface">
          {anyFilter ? (
            <EmptyState
              title="No visit matches these filters."
              action={
                <button
                  type="button"
                  onClick={() =>
                    patchParams((patch) => {
                      for (const key of ['user_id', 'purpose', 'open', 'from', 'to']) {
                        patch.delete(key);
                      }
                    })
                  }
                  className="text-body13 font-medium text-indigo hover:underline"
                >
                  Clear the filters.
                </button>
              }
            />
          ) : (
            <EmptyState title="No visits have been recorded in this country yet." />
          )}
        </div>
      ) : (
        <VisitTable
          countryCode={countryCode}
          rows={rows}
          onEngineer={(id) => setParamOrDrop('user_id', id)}
        />
      )}

      {list.data !== undefined && rows.length > 0 ? (
        <PagerFooter
          page={list.data.page}
          perPage={list.data.perPage}
          shown={rows.length}
          total={list.data.total}
          onPage={goToPage}
        />
      ) : null}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── table ── */

const HEADERS = ['ENGINEER', 'CUSTOMER', 'PURPOSE', 'ARRIVED', 'LEFT', 'ARRIVAL CHECK'] as const;

/**
 * ⚠ THE DAY THE TIMELINE LINK NAMES IS A DERIVATION, AND THE LEAST-WRONG ONE
 * (recorded decision). The timeline route wants the COUNTRY-local day, which
 * the attendance row carries (`work_date`, server-computed) but the visit row
 * does not — and this unit owns `apps/web` only, so the wire cannot grow one
 * tonight. A UTC slice of `arrive_at` is wrong by a whole day for every
 * evening visit in India (the timeline's own e2e pins that 20:30 UTC lands on
 * the NEXT IN day); the VIEWER-local day is exact whenever the viewer is in
 * the country's own timezone, which is this product's normal case. The
 * timeline page's prev/next-day controls cover the residue. Flagged for
 * ratification: the durable fix is `FieldVisitDto` carrying the country-local
 * day the way attendance does.
 */
function dayOfIso(iso: string): string {
  const date = new Date(iso);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${String(date.getFullYear())}-${month}-${day}`;
}

function VisitTable({
  countryCode,
  rows,
  onEngineer,
}: {
  countryCode: string;
  rows: readonly VisitRow[];
  onEngineer: (userId: string) => void;
}): React.JSX.Element {
  return (
    <div className="overflow-x-auto rounded-md border border-line bg-surface">
      <table className="w-full min-w-[1180px] border-collapse text-left">
        <caption className="sr-only">Visits recorded in {countryCode}</caption>

        <thead>
          <tr className="h-10 border-b border-line bg-subtle">
            {HEADERS.map((header) => (
              <th key={header} scope="col" className="px-3 text-th uppercase text-fg-muted">
                {header}
              </th>
            ))}
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              POSITIONS
            </th>
            <th scope="col" className="px-3">
              <span className="sr-only">Row actions</span>
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => {
            const pill = agreementPill(row.arrival_agreement);
            return (
              <tr
                key={row.id}
                className="crf-register-row border-b border-line last:border-b-0 hover:bg-subtle"
              >
                <td className="px-3 py-3 align-middle">
                  <button
                    type="button"
                    onClick={() => onEngineer(row.user_id)}
                    title="Filter the register to this engineer"
                    className="text-body13 font-medium text-fg underline-offset-2 hover:underline"
                  >
                    {row.user_name ?? `Engineer #${row.user_id}`}
                  </button>
                </td>

                <td className="px-3 py-3 align-middle text-body13 text-fg">
                  {row.party_name ?? `Party #${row.party_id}`}
                </td>

                <td className="px-3 py-3 align-middle text-body13 capitalize text-fg-secondary">
                  {row.purpose}
                </td>

                <td className="px-3 py-3 align-middle">
                  <TimeCell iso={row.arrive_at} />
                </td>

                <td className="px-3 py-3 align-middle">
                  {row.leave_at !== null ? (
                    <TimeCell iso={row.leave_at} />
                  ) : (
                    <span
                      data-visit-state="on-site"
                      className="inline-flex items-center whitespace-nowrap rounded-pill bg-info-bg px-2.5 py-1 text-label font-medium text-info-fg"
                    >
                      On site
                    </span>
                  )}
                </td>

                <td className="px-3 py-3 align-middle">
                  <span className="flex items-center gap-2">
                    <span
                      /* The classified KIND, not the raw token — otherwise a
                         masked row ships `data-agreement="•••"` into the DOM
                         (and into every test selector) as if the marker were a
                         verdict. 'agrees'/'disagrees'/'undeterminable' are
                         unchanged; 'restricted' is the new one. */
                      data-agreement={pill.kind}
                      title={
                        pill.kind === 'restricted'
                          ? 'Whether the position matched the saved location is restricted for your account (•••) — the recorded band is shown beside it'
                          : undefined
                      }
                      className={cn(
                        'inline-flex items-center whitespace-nowrap rounded-pill px-2.5 py-1 text-label font-medium',
                        pill.classes,
                      )}
                    >
                      {pill.label}
                    </span>
                    {/* v17 F-1: raw metres for self / a `field.location.view`
                        holder, the coarse band for every other seat. The cell
                        owns that choice so the register never sees the mask. */}
                    <ArrivalDistance
                      value={row.arrival_distance_m}
                      band={row.arrival_distance_band}
                    />
                  </span>
                </td>

                <td className="px-3 py-3 align-middle">
                  <span className="flex flex-col gap-1">
                    <PositionCell value={row.arrive_position} />
                    {row.leave_at !== null ? <PositionCell value={row.leave_position} /> : null}
                  </span>
                </td>

                <td className="px-3 py-3 align-middle">
                  <Link
                    to={`/countries/${countryCode}/field/timeline?user_id=${row.user_id}&date=${dayOfIso(row.arrive_at)}`}
                    state={{ engineerName: row.user_name }}
                    className="text-body13 font-medium text-fg underline underline-offset-2 hover:decoration-2"
                  >
                    Day timeline
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
